import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Download, FolderOpen, Trash2, Plus, Loader2, FileText, Pin, Printer, RefreshCw, Sparkles, MessageSquareText, ListChecks, NotebookPen } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ivApi, downloadMarkdown } from "@/lib/interviewApi";

const when = (iso) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
const MARK = { covered: "✓", touched: "◐", open: "○" };

const InterviewDetail = ({ iv, onBack, onLoad, onDelete }) => {
  const segs = [...iv.segments].sort((a, b) => a.ts - b.ts);
  return (
    <div className="flex flex-col gap-4" data-testid="library-detail">
      <button onClick={onBack} data-testid="library-back-button" className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white w-fit"><ArrowLeft size={14} /> All interviews</button>
      <div>
        <h3 className="font-display text-xl font-bold">{iv.guest_name || "Untitled guest"}</h3>
        <p className="text-sm text-slate-300">{iv.topic || "No topic"} · {when(iv.updated_at)}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => onLoad(iv)} data-testid="library-load-button" className="flex items-center gap-1.5 rounded-full bg-sky-400 text-[#070F1E] font-semibold px-3 py-1.5 text-sm hover:bg-sky-300 transition-colors"><FolderOpen size={14} /> Open in workspace</button>
        <button onClick={() => downloadMarkdown(iv)} data-testid="library-export-button" className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-sm hover:bg-white/20 transition-colors"><Download size={14} /> Export</button>
        <button onClick={() => onDelete(iv.id)} data-testid="library-delete-button" className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-rose-300 hover:bg-rose-500/15 transition-colors"><Trash2 size={14} /> Delete</button>
      </div>
      <div>
        <p className="eyebrow mb-2">Shortlist</p>
        {iv.pinned.length === 0 ? <p className="text-sm text-slate-300">No pinned questions.</p> : <ul className="space-y-1.5">{iv.pinned.map((p) => <li key={p.id} className={`text-sm ${p.asked ? "line-through text-slate-400" : "text-white"}`}>• {p.text}</li>)}</ul>}
      </div>
      {iv.topics.length > 0 && <div><p className="eyebrow mb-2">Coverage</p><p className="text-sm text-slate-200">{iv.topics.map((t) => `${MARK[t.status]} ${t.label}`).join("   ")}</p></div>}
      <div>
        <p className="eyebrow mb-2">Transcript</p>
        <div className="space-y-3" data-testid="library-transcript">
          {segs.length === 0 && <p className="text-sm text-slate-300">Empty transcript.</p>}
          {segs.map((s) => (
            <p key={s.id} className="text-sm leading-relaxed text-white">
              <span className={`font-mono text-[0.78rem] uppercase tracking-widest mr-2 ${s.speaker === "guest" ? "text-emerald-300" : "text-sky-300"}`}>{s.speaker === "guest" ? iv.guest_name || "Guest" : "You"}</span>{s.text}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};

export const LibrarySheet = ({ open, onOpenChange, onLoad, onNew, currentId }) => {
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!open) return;
    setDetail(null);
    ivApi.listInterviews().then(setItems).catch((e) => toast.error(e.message));
  }, [open]);
  const openItem = (id) => ivApi.getInterview(id).then(setDetail).catch((e) => toast.error(e.message));
  const remove = async (id) => {
    if (!window.confirm("Delete this saved interview permanently?")) return;
    try { await ivApi.deleteInterview(id); } catch (e) { return toast.error(e.message); }
    setItems((xs) => xs.filter((x) => x.id !== id));
    setDetail(null);
    toast.success("Interview deleted");
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg bg-[#0F1C33] border-white/10 text-white overflow-y-auto" data-testid="library-sheet">
        <SheetHeader><SheetTitle className="font-display text-white text-left">Saved interviews</SheetTitle></SheetHeader>
        <div className="mt-5">
          {detail ? <InterviewDetail iv={detail} onBack={() => setDetail(null)} onLoad={onLoad} onDelete={remove} /> : (
            <div className="space-y-2">
              <button onClick={onNew} data-testid="library-new-button" className="flex w-full items-center gap-2 rounded-lg border border-dashed border-white/20 px-4 py-3 text-sm text-slate-300 hover:border-sky-400/60 hover:text-white transition-colors"><Plus size={15} /> Start a new interview</button>
              {items === null && <Loader2 className="animate-spin text-slate-300 mx-auto mt-6" />}
              {items?.length === 0 && <p className="text-sm text-slate-300 py-4" data-testid="library-empty">Nothing saved yet. Use Save in the top bar — interviews also save automatically when you press Stop.</p>}
              {items?.map((it) => (
                <button key={it.id} onClick={() => openItem(it.id)} data-testid="library-item" className="w-full text-left rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 hover:border-white/25 hover:bg-white/[0.06] transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-white truncate">{it.guest_name || "Untitled guest"}</span>
                    {it.id === currentId && <span className="text-[0.72rem] font-mono uppercase tracking-widest text-sky-300">current</span>}
                  </div>
                  <p className="text-sm text-slate-300 truncate">{it.topic || "No topic"}</p>
                  <p className="mt-1 text-[0.78rem] font-mono text-slate-400">{when(it.updated_at)} · {it.turns} lines · {it.pinned} pinned</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const Section = ({ title, children }) => <div className="break-inside-avoid"><h4 className="eyebrow mb-2 print:text-black">{title}</h4>{children}</div>;

const QuestionList = ({ items, isPinned, onPin, testId }) => (
  <ol className="space-y-2" data-testid={testId}>
    {items.map((q, i) => (
      <li key={q} className="flex items-start gap-2">
        <span className="font-mono text-sm text-slate-400 mt-0.5">{i + 1}.</span>
        <p className="flex-1 text-base leading-snug text-white print:text-black">{q}</p>
        <button onClick={() => onPin(q)} data-testid="brief-pin-button" title="Pin to shortlist" className={`iv-icon-btn print:hidden ${isPinned(q) ? "text-amber-400" : ""}`}><Pin size={15} fill={isPinned(q) ? "currentColor" : "none"} /></button>
      </li>
    ))}
  </ol>
);

const BriefBody = ({ brief, isPinned, onPin }) => (
  <div className="space-y-6">
    <p className="text-lg leading-relaxed text-white print:text-black" data-testid="brief-snapshot">{brief.snapshot}</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Section title="Key facts"><ul className="space-y-1.5 text-base text-white print:text-black" data-testid="brief-key-facts">{brief.key_facts.map((f) => <li key={f}>• {f}</li>)}</ul></Section>
      <Section title="Likely angles">
        <ul className="space-y-2.5" data-testid="brief-angles">
          {brief.angles.map((a) => <li key={a.title}><p className="text-base font-semibold text-sky-300 print:text-black">{a.title}</p><p className="text-sm text-slate-200 print:text-black">{a.why}</p></li>)}
        </ul>
      </Section>
    </div>
    <Section title="Opening questions"><QuestionList items={brief.openers} isPinned={isPinned} onPin={onPin} testId="brief-openers" /></Section>
    <Section title="Go deeper later"><QuestionList items={brief.deeper} isPinned={isPinned} onPin={onPin} testId="brief-deeper" /></Section>
    {brief.watch_outs.length > 0 && <Section title="Watch outs"><ul className="space-y-1.5 text-base text-amber-200 print:text-black" data-testid="brief-watch-outs">{brief.watch_outs.map((w) => <li key={w}>• {w}</li>)}</ul></Section>}
  </div>
);

export const BriefDialog = ({ open, onOpenChange, brief, loading, onGenerate, setup, isPinned, onPin }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="print-area max-w-3xl max-h-[90vh] overflow-y-auto bg-[#0F1C33] border-white/15 text-white" data-testid="brief-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl text-left print:text-black">Prep brief{setup.guest_name ? ` · ${setup.guest_name}` : ""}</DialogTitle>
        {setup.topic && <p className="text-base text-slate-300 print:text-black">{setup.topic}</p>}
      </DialogHeader>
      <div className="flex flex-wrap gap-2 print:hidden">
        <button onClick={onGenerate} disabled={loading || !setup.notes.trim()} data-testid="brief-generate-button" className="flex items-center gap-2 rounded-full bg-sky-400 text-[#070F1E] font-semibold px-4 py-2 text-sm hover:bg-sky-300 disabled:opacity-40 transition-colors">
          {loading ? <Loader2 size={15} className="animate-spin" /> : brief ? <RefreshCw size={15} /> : <FileText size={15} />}
          {loading ? "Writing brief..." : brief ? "Rewrite from notes" : "Write my brief"}
        </button>
        {brief && <button onClick={() => { document.body.classList.add("printing-brief"); window.print(); document.body.classList.remove("printing-brief"); }} data-testid="brief-print-button" className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm hover:bg-white/20 transition-colors"><Printer size={15} /> Print / PDF</button>}
      </div>
      {!brief && !loading && <p className="text-base text-slate-300" data-testid="brief-empty">{setup.notes.trim() ? "Turn your prep notes into a one-page brief: who they are, key facts, angles, openers and watch outs." : "Add prep notes first, then write your brief."}</p>}
      {brief && <BriefBody brief={brief} isPinned={isPinned} onPin={onPin} />}
    </DialogContent>
  </Dialog>
);

const TABS = [["suggestions", "Ask", Sparkles], ["transcript", "Live", MessageSquareText], ["coverage", "Topics", ListChecks], ["pinned", "Pins", Pin], ["setup", "Prep", NotebookPen]];

export const CompactLayout = ({ panels, counts }) => {
  const [tab, setTab] = useState("suggestions");
  return (
    <div className="flex flex-col" data-testid="compact-layout">
      <nav className="sticky top-[calc(var(--hdr,49px)+0.5rem)] z-10 grid grid-cols-5 border-b border-white/10 bg-[#0B1527]" data-testid="compact-tabs">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`compact-tab-${key}`} className={`flex flex-col items-center gap-0.5 py-2 text-[0.78rem] font-medium transition-colors border-b-2 ${tab === key ? "border-sky-400 text-white" : "border-transparent text-slate-300 hover:text-white"}`}>
            <Icon size={15} />
            <span>{label}{counts[key] ? ` ${counts[key]}` : ""}</span>
            {key === "suggestions" && counts.wrap && tab !== key && <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" data-testid="compact-wrap-badge" />}
          </button>
        ))}
      </nav>
      <main className="py-3 flex flex-col gap-3">
        {tab === "suggestions" ? <>{panels.talk}{panels.wrap}{panels.suggestions}{panels.glance}{panels.pinned}</>
          : tab === "transcript" ? <>{panels.talk}{panels.transcript}</>
          : tab === "coverage" ? <>{panels.pacing}{panels.coverage}</>
          : panels[tab]}
      </main>
    </div>
  );
};
